// covered-by: frontends/carrie/src/ask-v2/review/__tests__/versionChanges.test.ts
//
// SPIKE (side-by-side version compare). What the chat already knows about the
// difference between two versions on screen: the comparison warhol made of
// that pair, its numbered windows, and whether a finding sits inside one.
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { type AskPunttReviewComment } from "@mg/schemas/src/christo/askPunttReview";
import {
  type AskPunttComparisonRegion,
  type AskPunttVersionComparison,
} from "@mg/schemas/src/christo/versionComparison";

/** The id a changed window is drawn under when it is stepped to. */
export const COMPARE_CHANGE_PREFIX = "compare-change-";

/**
 * The latest comparison the chat holds of these two files, whichever of the
 * two is the older. Null when the pair was never compared.
 *
 * Two versions uploaded under one name cannot be told apart by name: then
 * only a comparison from the turn that wrote the newer version's review
 * counts, and none when that review is not known.
 */
export function comparisonBetween(
  messages: readonly Pick<AskPunttMessage, "comparisons" | "viewReviewId">[],
  fileA: string | undefined,
  fileB: string | undefined,
  newerReviewId?: string,
): AskPunttVersionComparison | null {
  if (!fileA || !fileB) return null;
  const sameName = fileA === fileB;
  if (sameName && !newerReviewId) return null;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (sameName && message.viewReviewId !== newerReviewId) continue;
    const found = (message.comparisons ?? []).find(
      (comparison) =>
        (comparison.before === fileA && comparison.after === fileB) ||
        (comparison.before === fileB && comparison.after === fileA),
    );
    if (found) return found;
  }
  return null;
}

// why: a pin sits on the glyphs and a window is cut tight around the changed
// words, so a finding about a changed line can land a hair outside its box.
const WINDOW_MARGIN = 0.01;

/** The changed window a finding sits in, or null when its place did not change. */
export function regionAt(
  regions: readonly AskPunttComparisonRegion[],
  comment: Pick<AskPunttReviewComment, "page_num" | "rel_x" | "rel_y" | "bbox">,
): AskPunttComparisonRegion | null {
  const x = comment.bbox ? comment.bbox.x + comment.bbox.w / 2 : comment.rel_x;
  const y = comment.bbox ? comment.bbox.y + comment.bbox.h / 2 : comment.rel_y;
  if (x == null || y == null) return null;
  const page = comment.page_num ?? 1;
  return (
    regions.find(
      (region) =>
        region.page === page &&
        x >= region.box.x - WINDOW_MARGIN &&
        x <= region.box.x + region.box.width + WINDOW_MARGIN &&
        y >= region.box.y - WINDOW_MARGIN &&
        y <= region.box.y + region.box.height + WINDOW_MARGIN,
    ) ?? null
  );
}

/** One edit as text; a move prints the same words on both sides, so once. */
function describeChange(
  change: AskPunttComparisonRegion["changes"][number],
): string {
  if (change.kind === "added") return `+ "${change.after}"`;
  if (change.kind === "removed") return `− "${change.before}"`;
  if (change.kind === "moved") return `"${change.before || change.after}"`;
  return `"${change.before}" → "${change.after}"`;
}

/** What changed in a window, as one line: `"962" → "1000"; added "…"`. */
export function describeRegion(region: AskPunttComparisonRegion): string {
  return region.changes.map(describeChange).join("; ");
}

/**
 * A changed window as a pin both viewers can move to. The viewer zooms to a
 * comment's `bbox`, so a window dressed as one is framed like a finding is.
 */
export function changePin(
  region: AskPunttComparisonRegion,
): AskPunttReviewComment {
  return {
    _id: `${COMPARE_CHANGE_PREFIX}${region.number}`,
    description: describeRegion(region),
    isAI: true,
    isDeleted: false,
    page_num: region.page,
    rel_x: region.box.x + region.box.width / 2,
    rel_y: region.box.y + region.box.height / 2,
    bbox: {
      x: region.box.x,
      y: region.box.y,
      w: region.box.width,
      h: region.box.height,
    },
  } as AskPunttReviewComment;
}

/** One line of a window's text diff, as the card draws it. */
export type DiffRow = {
  /** `removed` red with −, `added` green with +, `moved` neutral, `context` gray. */
  kind: "removed" | "added" | "moved" | "context";
  text: string;
};

/**
 * A window's edits as diff lines: a replacement is its old words then its new
 * ones, the way a code diff reads; the words around an edit follow it as
 * context. Only what warhol listed -- `changesMore` counts the rest.
 */
export function diffRows(region: AskPunttComparisonRegion): DiffRow[] {
  const rows: DiffRow[] = [];
  for (const change of region.changes) {
    if (change.kind === "replaced" || change.kind === "removed") {
      rows.push({ kind: "removed", text: change.before });
    }
    if (change.kind === "replaced" || change.kind === "added") {
      rows.push({ kind: "added", text: change.after });
    }
    if (change.kind === "moved") {
      rows.push({ kind: "moved", text: change.before || change.after });
    }
    if (change.near.trim()) rows.push({ kind: "context", text: change.near });
  }
  return rows;
}

/**
 * What a window's card is titled by: its first edit, or null for a window
 * with no listed words -- a picture that changed, or words that moved -- which
 * the card names by its evidence instead.
 */
export function regionTitle(region: AskPunttComparisonRegion): string | null {
  const [first] = region.changes;
  return first ? describeChange(first) : null;
}

/**
 * A pin's tone on one side of a comparison: a changed window reads red on the
 * older version and green on the newer one; every other pin keeps its color.
 */
type PinTone = (comment: { _id: string }) => "before" | "after" | null;

const toneOf =
  (side: "before" | "after" | null): PinTone =>
  (comment) =>
    side && comment._id.startsWith(COMPARE_CHANGE_PREFIX) ? side : null;

// why one function per side: the tone is a context value, and a fresh one each
// render re-renders every pin on every poll of a running review.
const PIN_TONES = {
  before: toneOf("before"),
  after: toneOf("after"),
  none: toneOf(null),
} as const;

export function comparePinTone(side: "before" | "after" | null): PinTone {
  return PIN_TONES[side ?? "none"];
}
